/**
 * The question-and-answer block and the JSON-LD that goes with it.
 *
 * The two rules pages were the first to carry a `FAQPage` and a breadcrumb; the
 * level and writing pages carry them too, so they live here rather than being
 * copied. The schema is built from the questions already on the page, never
 * from a second copy of them.
 */
import { Link } from "@/components/link";

/** A question that opens, and the answer under it. */
function Question({ q, a }: { q: string; a: string }) {
  return (
    <details className="group py-3.5">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 marker:content-none">
        <span className="text-[0.95rem] font-medium">{q}</span>
        <span
          aria-hidden
          className="shrink-0 text-muted-foreground transition-transform group-open:rotate-45"
        >
          +
        </span>
      </summary>
      <p className="mt-2 max-w-[66ch] text-sm leading-relaxed text-muted-foreground">{a}</p>
    </details>
  );
}

export function Faq({ title, items }: { title?: string; items: { q: string; a: string }[] }) {
  return (
    <section>
      {title && <h2 className="mb-3 text-xl font-bold tracking-tight">{title}</h2>}
      <div className="flex flex-col divide-y rounded-xl border px-4">
        {items.map((item) => (
          <Question key={item.q} q={item.q} a={item.a} />
        ))}
      </div>
    </section>
  );
}

/** The trail back up to the exam this page explains — the site's only two-level
 *  URLs, and the only place a breadcrumb has anything to say. */
export function Crumbs({ to, label }: { to: string; label: string }) {
  return (
    <nav aria-label="breadcrumb" className="mb-3 text-sm text-muted-foreground">
      <Link to={to} className="hover:text-foreground hover:underline">
        {label}
      </Link>
    </nav>
  );
}

/** `FAQPage` from the questions already on the page — no second copy of them. */
export const faqSchema = (items: { q: string; a: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: item.a },
  })),
});

export const crumbSchema = (parent: { name: string; url: string }, here: string) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: parent.name, item: parent.url },
    { "@type": "ListItem", position: 2, name: here },
  ],
});
